import React, { useRef, useEffect, useLayoutEffect, useState, useMemo, useCallback } from "react";
// @ts-expect-error react-cytoscapejs has no official types
import CytoscapeComponent from "react-cytoscapejs";
import { Core, ElementDefinition } from "cytoscape";
import {
  ArrowDownTrayIcon,
  ArrowsPointingOutIcon,
  ArrowsPointingInIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  MagnifyingGlassPlusIcon,
  MagnifyingGlassMinusIcon,
  ViewfinderCircleIcon,
} from "@heroicons/react/24/outline";
import { Path, PathRelationship } from "../../services/mechanismsClient";
import { useTheme } from "../../theme/useTheme";

interface PathwayGraphProps {
  pathData: Path | undefined;
}

/** Node labels (genes, diseases, pathways): larger for readability. */
const NODE_LABEL_FONT_SIZE = 14;
/** Edge relationship text: smaller so it does not compete with nodes; placed near disease when possible. */
const EDGE_LABEL_FONT_SIZE = 10;
/** Minimum model-space distance between gene node centers after layout (prevents overlap). */
const MIN_GENE_CENTER_SEPARATION = 150;

/** Padding around the fitted node bounding box (see cy.fit). */
const FIT_PADDING = 8;

/** UI buckets for search / filter (aligned with screenshot). */
type NodeFilterCategory = "gene" | "disease" | "go" | "phenotype" | "pathway" | "other";

const NODE_TYPE_FILTER_ROWS: { key: NodeFilterCategory; label: string }[] = [
  { key: "gene", label: "Genes" },
  { key: "disease", label: "Diseases" },
  { key: "go", label: "GO" },
  { key: "phenotype", label: "Phenotypes" },
  { key: "pathway", label: "Pathways" },
];

function nodeCategoryFromApiType(nodeType: string): NodeFilterCategory {
  const nt = (nodeType || "").trim().toLowerCase();
  if (nt === "gene") return "gene";
  if (nt.includes("disease")) return "disease";
  if (nt.includes("phenotype")) return "phenotype";
  if (nt.includes("pathway")) return "pathway";
  if (nt.includes("go") || nt.replace(/\s/g, "") === "goterm") return "go";
  return "other";
}

function parseSearchTerms(query: string): string[] {
  return query
    .split(",")
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
}

function deriveGraphFilterMetadata(pathData: Path): {
  categoryCounts: Record<NodeFilterCategory, number>;
  edgeCaptions: string[];
} {
  const categoryCounts: Record<NodeFilterCategory, number> = {
    gene: 0,
    disease: 0,
    go: 0,
    phenotype: 0,
    pathway: 0,
    other: 0,
  };
  pathData.nodes.forEach((n) => {
    const cat = nodeCategoryFromApiType(n.properties?.node_type || "");
    categoryCounts[cat] += 1;
  });

  const captions = new Set<string>();
  const els = buildCytoscapeElements(pathData);
  els.forEach((el) => {
    const d = el.data as { source?: string; caption?: string };
    if (d.source && d.caption != null && String(d.caption).trim() !== "") {
      captions.add(String(d.caption).trim());
    }
  });

  return {
    categoryCounts,
    edgeCaptions: Array.from(captions).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" })),
  };
}

/** Human-readable label for an API `node_type` (keeps values like `GO` readable). */
function displayNodeTypeName(raw: string): string {
  const s = (raw || "Unknown").trim();
  if (!s) return "Unknown";
  return s.replace(/_/g, " ");
}

/** Group neighbors: genes, diseases, then each remaining type under its original `node_type` name. */
function bucketConnectedNeighbors(
  pathData: Path,
  neighborIds: string[]
): {
  genes: string[];
  diseases: string[];
  /** One section per distinct non-gene, non-disease type (e.g. GO, Pathway). */
  otherByType: { typeKey: string; displayName: string; labels: string[] }[];
} {
  const genes: string[] = [];
  const diseases: string[] = [];
  const otherMap = new Map<string, string[]>();

  neighborIds.forEach((nid) => {
    const n = pathData.nodes.find((x) => x.id === nid);
    const label = n?.caption?.trim() || nid;
    const rawType = (n?.properties?.node_type || "Unknown").trim() || "Unknown";
    const nt = rawType.toLowerCase();
    if (nt === "gene") {
      genes.push(label);
    } else if (nt.includes("disease")) {
      diseases.push(label);
    } else {
      if (!otherMap.has(rawType)) otherMap.set(rawType, []);
      otherMap.get(rawType)!.push(label);
    }
  });

  const otherByType = Array.from(otherMap.entries())
    .map(([typeKey, labels]) => ({
      typeKey,
      displayName: displayNodeTypeName(typeKey),
      labels,
    }))
    .sort((a, b) => a.displayName.localeCompare(b.displayName, undefined, { sensitivity: "base" }));

  return { genes, diseases, otherByType };
}

function typeBadgeClassName(nodeType: string): string {
  const nt = (nodeType || "").toLowerCase();
  if (nt === "gene") {
    return "bg-amber-100 text-amber-950 dark:bg-amber-900/50 dark:text-amber-100";
  }
  if (nt.includes("disease")) {
    return "bg-rose-100 text-rose-950 dark:bg-rose-900/50 dark:text-rose-100";
  }
  if (nt.includes("pathway")) {
    return "bg-indigo-100 text-indigo-950 dark:bg-indigo-900/50 dark:text-indigo-100";
  }
  if (nt.includes("go") || nt.startsWith("go")) {
    return "bg-emerald-100 text-emerald-950 dark:bg-emerald-900/50 dark:text-emerald-100";
  }
  return "bg-slate-200 text-slate-800 dark:bg-slate-600 dark:text-slate-100";
}

/** Resolve relationship endpoints (API may use Neo4j-style field names). */
function relationshipEndpoints(
  rel: PathRelationship
): { source: string; target: string } | null {
  const source = rel.source ?? rel.startNode ?? rel.from;
  const target = rel.target ?? rel.endNode ?? rel.to;
  if (!source || !target) return null;
  return { source, target };
}

/** Prefer `color` from the API; fall back to screenshot-like palette (genes gold, periphery coral/orange). */
function resolveNodeFillColor(
  raw: string | undefined,
  nodeType: string,
  isDark: boolean
): string {
  if (raw != null && String(raw).trim() !== "") {
    const s = String(raw).trim();
    if (s.startsWith("#")) return s;
    if (s.startsWith("rgb") || s.startsWith("hsl")) return s;
    if (/^[0-9a-fA-F]{3,8}$/.test(s)) return `#${s}`;
    return s;
  }
  const nt = (nodeType || "").toLowerCase();
  if (nt === "gene") return isDark ? "#ca8a04" : "#fde047";
  if (nt.includes("disease")) return isDark ? "#9f1239" : "#fda4af";
  if (nt.includes("go") || nt.startsWith("go")) return isDark ? "#047857" : "#86efac";
  if (nt.includes("pathway")) return isDark ? "#9a3412" : "#fdba74";
  return isDark ? "#475569" : "#fecdd3";
}

/** After CoSE, push gene nodes apart if the solver stacked them (common with 2 genes + many leaves). */
function separateOverlappingGeneNodes(cy: Core) {
  const genes = cy.nodes('[graphStyle = "gene"]');
  if (genes.length < 2) return;

  const minSep = MIN_GENE_CENTER_SEPARATION;
  for (let i = 0; i < genes.length; i++) {
    for (let j = i + 1; j < genes.length; j++) {
      const a = genes[i];
      const b = genes[j];
      const pa = a.position();
      const pb = b.position();
      const dx = pb.x - pa.x;
      const dy = pb.y - pa.y;
      const dist = Math.hypot(dx, dy);
      if (dist < 1e-4) {
        a.position({ x: pa.x - minSep / 2, y: pa.y });
        b.position({ x: pa.x + minSep / 2, y: pa.y });
        continue;
      }
      if (dist < minSep) {
        const push = (minSep - dist) / 2 + 6;
        const ux = dx / dist;
        const uy = dy / dist;
        a.position({ x: pa.x - ux * push, y: pa.y - uy * push });
        b.position({ x: pb.x + ux * push, y: pb.y + uy * push });
      }
    }
  }
}

/** Cytoscape role for styling (aligned with `ComparisonNetworkGraph` gene / pathway / other). */
function graphStyleForNodeType(nodeType: string): "gene" | "pathway" | "entity" {
  const nt = (nodeType || "").toLowerCase();
  if (nt === "gene") return "gene";
  if (nt.includes("pathway")) return "pathway";
  return "entity";
}

/** Build cytoscape elements: edges carry `edgeType` for styles aligned with `ComparisonNetworkGraph`. */
function buildCytoscapeElements(pathData: Path): ElementDefinition[] {
  const elements: ElementDefinition[] = [];
  const nodes = pathData.nodes;
  const geneNodes = nodes.filter((n) => n.properties?.node_type === "Gene");
  const diseaseNodes = nodes.filter((n) => n.properties?.node_type !== "Gene");

  nodes.forEach((n) => {
    const nodeType = n.properties?.node_type || "Unknown";
    elements.push({
      data: {
        id: n.id,
        label: n.caption,
        nodeType,
        graphStyle: graphStyleForNodeType(nodeType),
        description: n.properties?.description || "",
        color: n.color,
      },
    });
  });

  const edgeKey = (a: string, b: string) => (a < b ? `${a}::${b}` : `${b}::${a}`);
  const seen = new Set<string>();

  const addDirectedEdge = (source: string, target: string, caption: string, edgeKind: string) => {
    if (source === target) return;
    const k = edgeKey(source, target);
    if (seen.has(k)) return;
    seen.add(k);
    elements.push({
      data: {
        id: `e-${k}`,
        source,
        target,
        caption,
        edgeType: edgeKind,
      },
    });
  };

  if (pathData.relationships?.length) {
    pathData.relationships.forEach((rel: PathRelationship) => {
      const ends = relationshipEndpoints(rel);
      if (!ends) return;
      const cap = rel.caption || rel.type || "RELATED";
      const srcNode = nodes.find((n) => n.id === ends.source);
      const tgtNode = nodes.find((n) => n.id === ends.target);
      const srcGene = srcNode?.properties?.node_type === "Gene";
      const tgtGene = tgtNode?.properties?.node_type === "Gene";
      /** Route gene–disease styling so labels sit on the disease end (less hub clutter). */
      let edgeKind = "relationship";
      if ((srcGene && !tgtGene) || (!srcGene && tgtGene)) edgeKind = "gene-disease";
      addDirectedEdge(ends.source, ends.target, cap, edgeKind);
    });
  }

  geneNodes.forEach((g1, i) => {
    geneNodes.forEach((g2, j) => {
      if (i >= j) return;
      const a = g1.id;
      const b = g2.id;
      const source = a < b ? a : b;
      const target = a < b ? b : a;
      addDirectedEdge(source, target, "INTERACTS_WITH", "gene-gene");
    });
  });

  if (!pathData.relationships?.length) {
    geneNodes.forEach((g) => {
      diseaseNodes.forEach((d) => addDirectedEdge(g.id, d.id, "ASSOCIATED_WITH", "gene-disease"));
    });
  }

  return elements;
}

/**
 * When search text is active: only nodes matching text (among enabled categories) are highlighted;
 * edges are hidden until a node is tapped (then neighborhood edges except `relationship` type).
 * Generic API `relationship` edges stay fully hidden while search terms are present.
 */
function computeSearchHighlightSets(
  pathData: Path,
  searchTerms: string[],
  selectedNodeCategories: Set<NodeFilterCategory>
): { highlightNodeIds: Set<string>; searchSeeds: Set<string> } {
  const empty = () => ({ highlightNodeIds: new Set<string>(), searchSeeds: new Set<string>() });
  if (searchTerms.length === 0) return empty();

  const nodeById = new Map(pathData.nodes.map((n) => [n.id, n]));
  const categoryOk = (id: string) => {
    const n = nodeById.get(id);
    if (!n) return false;
    return selectedNodeCategories.has(nodeCategoryFromApiType(n.properties?.node_type || ""));
  };
  const textMatch = (id: string) => {
    const n = nodeById.get(id);
    if (!n) return false;
    const label = (n.caption || "").toLowerCase();
    const nid = id.toLowerCase();
    return searchTerms.some((t) => label.includes(t) || nid.includes(t));
  };

  const searchSeeds = new Set<string>();
  pathData.nodes.forEach((n) => {
    if (categoryOk(n.id) && textMatch(n.id)) searchSeeds.add(n.id);
  });

  return { highlightNodeIds: searchSeeds, searchSeeds };
}

/** Pathway network graph: CoSE force layout, straight edges with captions (screenshot-style). */
const PathwayGraphInner: React.FC<{ pathData: Path }> = ({ pathData }) => {
  const themeCtx = useTheme();
  const cyRef = useRef<Core | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  /** Surface that actually sizes the canvas (resize observer target). */
  const graphSurfaceRef = useRef<HTMLDivElement>(null);
  /** Attach listeners once per Cytoscape instance (cy callback runs on every prop update). */
  const eventListenersCyRef = useRef<Core | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [tapFocusNodeId, setTapFocusNodeId] = useState<string | null>(null);
  const [tapNeighborIds, setTapNeighborIds] = useState<string[]>([]);
  const [searchPanelOpen, setSearchPanelOpen] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [selectedNodeCategories, setSelectedNodeCategories] = useState<Set<NodeFilterCategory>>(() => {
    const meta = deriveGraphFilterMetadata(pathData);
    const presentCats = (Object.entries(meta.categoryCounts) as [NodeFilterCategory, number][])
      .filter(([, c]) => c > 0)
      .map(([k]) => k);
    return new Set(presentCats);
  });
  const [selectedEdgeCaptions, setSelectedEdgeCaptions] = useState<Set<string>>(() => {
    return new Set(deriveGraphFilterMetadata(pathData).edgeCaptions);
  });
  const searchPanelRef = useRef<HTMLDivElement>(null);
  const searchButtonRef = useRef<HTMLButtonElement>(null);

  const filterMeta = useMemo(() => deriveGraphFilterMetadata(pathData), [pathData]);
  const searchTerms = useMemo(() => parseSearchTerms(searchInput), [searchInput]);

  const elements = useMemo(() => buildCytoscapeElements(pathData), [pathData]);

  const { highlightNodeIds, searchSeeds } = useMemo(
    () => computeSearchHighlightSets(pathData, searchTerms, selectedNodeCategories),
    [pathData, searchTerms, selectedNodeCategories]
  );

  /**
   * Sync renderer size to the graph surface, then fit to **nodes** (edges/labels can inflate bbox).
   * Double rAF so flex/layout after fullscreen exit has committed before `cy.resize()` reads dimensions.
   */
  const applyViewportFit = useCallback((cy: Core) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const surface = graphSurfaceRef.current;
        if (surface) {
          void surface.offsetWidth;
          void surface.getBoundingClientRect();
        }
        cy.resize();
        const nodes = cy.nodes();
        if (nodes.length > 0) {
          cy.fit(nodes, FIT_PADDING);
        } else {
          cy.fit(undefined, FIT_PADDING);
        }
      });
    });
  }, []);

  useLayoutEffect(() => {
    setTapFocusNodeId(null);
    setTapNeighborIds([]);
  }, [pathData.path]);

  useLayoutEffect(() => {
    setSearchInput("");
    const meta = deriveGraphFilterMetadata(pathData);
    const presentCats = (Object.entries(meta.categoryCounts) as [NodeFilterCategory, number][])
      .filter(([, c]) => c > 0)
      .map(([k]) => k);
    setSelectedNodeCategories(new Set(presentCats));
    setSelectedEdgeCaptions(new Set(meta.edgeCaptions));
  }, [pathData.path]);

  const selectedNodeDetails = useMemo(() => {
    if (!tapFocusNodeId) return null;
    const node = pathData.nodes.find((n) => n.id === tapFocusNodeId);
    if (!node) return null;
    const buckets = bucketConnectedNeighbors(pathData, tapNeighborIds);
    const totalConnected = tapNeighborIds.length;
    return { node, buckets, totalConnected };
  }, [tapFocusNodeId, tapNeighborIds, pathData]);

  const isDark = themeCtx.mode === "dark";

  const stylesheet = useMemo(() => {
    /** Same palette as `ComparisonNetworkGraph`. */
    const labelColor = isDark ? "#e2e8f0" : "#1e293b";
    /** Association / API edges: strong contrast on light backgrounds (pale blues were nearly invisible). */
    const edgeLinkBlue = isDark ? "#60a5fa" : "#1d4ed8";
    /** Gene–gene edges: darker amber on light mode for visibility. */
    const edgeLinkAmber = isDark ? "#fcd34d" : "#b45309";
    const edgeDim = isDark ? "#475569" : "#94a3b8";
    const tapFocusBorderCenter = isDark ? "#f8fafc" : "#0f172a";
    const tapFocusBorderNeighbor = isDark ? "#94a3b8" : "#475569";

    const inTapNeighborhood = (nodeId: string) =>
      !tapFocusNodeId || nodeId === tapFocusNodeId || tapNeighborIds.includes(nodeId);

    const isTapCenter = (nodeId: string) => tapFocusNodeId != null && nodeId === tapFocusNodeId;

    const edgeInTapNeighborhood = (ele: {
      source: () => { id: () => string };
      target: () => { id: () => string };
    }) => {
      if (!tapFocusNodeId) return false;
      const s = ele.source().id();
      const t = ele.target().id();
      return inTapNeighborhood(s) && inTapNeighborhood(t);
    };

    const edgeLabel = isDark ? "#94a3b8" : "#64748b";

    const nodePassesCategoryFilter = (ele: { id: () => string; data: (k: string) => string }) => {
      const nodeType = ele.data("nodeType") || "";
      const cat = nodeCategoryFromApiType(nodeType);
      return selectedNodeCategories.has(cat);
    };

    /** Full opacity / border: no search = category filter; search = text matches, or tap neighborhood when focused. */
    const nodeInSearchHighlight = (ele: { id: () => string; data: (k: string) => string }) => {
      if (!nodePassesCategoryFilter(ele)) return false;
      if (searchTerms.length === 0) return true;
      if (tapFocusNodeId && inTapNeighborhood(ele.id())) return true;
      return highlightNodeIds.has(ele.id());
    };

    const edgePassesGraphFilter = (ele: {
      data: (k: string) => string;
      source: () => { id: () => string; data: (k: string) => string };
      target: () => { id: () => string; data: (k: string) => string };
    }) => {
      const caption = String(ele.data("caption") || "").trim();
      if (!selectedEdgeCaptions.has(caption)) return false;
      if (!nodePassesCategoryFilter(ele.source()) || !nodePassesCategoryFilter(ele.target())) return false;
      /** Non–gene-disease API edges: off for the whole search session (simpler than faint lines after tap). */
      if (searchTerms.length > 0 && String(ele.data("edgeType") || "") === "relationship") return false;
      /** While searching, hide gene-disease / gene-gene edges until tap; then show only neighborhood edges. */
      if (searchTerms.length > 0) {
        return !!(tapFocusNodeId && edgeInTapNeighborhood(ele));
      }
      return true;
    };

    /** Cyan ring on text matches only (non-matching nodes stay dimmed). */
    const nodeSearchRing = (ele: { id: () => string; data: (k: string) => string }) =>
      searchTerms.length > 0 && searchSeeds.has(ele.id());

    const searchRingColor = isDark ? "#38bdf8" : "#2563eb";

    type EdgeEle = {
      data: (k: string) => string;
      source: () => { id: () => string; data: (k: string) => string };
      target: () => { id: () => string; data: (k: string) => string };
    };

    const edgeLineOpacity = (ele: EdgeEle) => {
      if (searchTerms.length > 0 && String(ele.data("edgeType") || "") === "relationship") return 0;
      if (searchTerms.length > 0 && !tapFocusNodeId) return 0;
      if (!edgePassesGraphFilter(ele)) return 0.1;
      if (!tapFocusNodeId) return 1;
      return edgeInTapNeighborhood(ele) ? 1 : 0.12;
    };

    const edgeTextOpacityGeneDisease = (ele: EdgeEle) => {
      if (searchTerms.length > 0 && !tapFocusNodeId) return 0;
      if (!edgePassesGraphFilter(ele)) return 0.08;
      if (!tapFocusNodeId) return 1;
      return edgeInTapNeighborhood(ele) ? 1 : 0.12;
    };

    const edgeTextOpacityGeneGene = (ele: EdgeEle) => {
      if (searchTerms.length > 0 && !tapFocusNodeId) return 0;
      if (!edgePassesGraphFilter(ele)) return 0.08;
      if (!tapFocusNodeId) return 0.85;
      return edgeInTapNeighborhood(ele) ? 0.95 : 0.12;
    };

    const edgeTextOpacityRelationship = (ele: EdgeEle) => {
      if (searchTerms.length > 0) return 0;
      if (!edgePassesGraphFilter(ele)) return 0.08;
      if (!tapFocusNodeId) return 0.9;
      return edgeInTapNeighborhood(ele) ? 0.95 : 0.12;
    };

    return [
      {
        selector: "core",
        style: {
          "background-color": isDark ? "#1e293b" : "#f8fafc",
        } as Record<string, unknown>,
      },
      {
        selector: "node",
        style: {
          label: "data(label)",
          "text-halign": "center",
          "font-family": "system-ui, -apple-system, sans-serif",
          "font-weight": 400,
          "text-wrap": "wrap",
          color: labelColor,
          opacity: (ele: { id: () => string; data: (k: string) => string }) => {
            if (!nodeInSearchHighlight(ele)) return 0.18;
            if (!tapFocusNodeId) return 1;
            if (inTapNeighborhood(ele.id())) return 1;
            /** Text matches stay fully visible while a different node is tapped (neighborhood vs search are independent). */
            if (searchTerms.length > 0 && searchSeeds.has(ele.id())) return 1;
            return 0.22;
          },
        } as Record<string, unknown>,
      },
      {
        selector: 'node[graphStyle = "pathway"]',
        style: {
          shape: "ellipse",
          padding: "6px",
          width: (ele: { id: () => string }) => {
            const base = 36;
            if (!tapFocusNodeId) return base;
            return isTapCenter(ele.id()) ? 44 : inTapNeighborhood(ele.id()) ? 40 : base;
          },
          height: (ele: { id: () => string }) => {
            const base = 36;
            if (!tapFocusNodeId) return base;
            return isTapCenter(ele.id()) ? 44 : inTapNeighborhood(ele.id()) ? 40 : base;
          },
          "background-color": (ele: { data: (k: string) => string }) => {
            const raw = ele.data("color");
            if (raw != null && String(raw).trim() !== "") {
              return resolveNodeFillColor(raw, "Pathway", isDark);
            }
            return isDark ? "#4f46e5" : "#6366f1";
          },
          "border-width": (ele: { id: () => string; data: (k: string) => string }) => {
            if (!nodeInSearchHighlight(ele)) return 0;
            if (tapFocusNodeId) {
              if (inTapNeighborhood(ele.id())) return 1;
              if (nodeSearchRing(ele)) return 3;
              return 0;
            }
            if (nodeSearchRing(ele)) return 3;
            return 0;
          },
          "border-color": (ele: { id: () => string; data: (k: string) => string }) => {
            if (!nodeInSearchHighlight(ele)) return isDark ? "#4f46e5" : "#6366f1";
            if (tapFocusNodeId) {
              if (isTapCenter(ele.id())) return tapFocusBorderCenter;
              if (inTapNeighborhood(ele.id())) return tapFocusBorderNeighbor;
            }
            if (nodeSearchRing(ele)) return searchRingColor;
            return isDark ? "#4f46e5" : "#6366f1";
          },
          color: labelColor,
          "text-valign": "top",
          "text-halign": "center",
          "text-margin-y": -2,
          "font-size": `${NODE_LABEL_FONT_SIZE}px`,
          "font-weight": 500,
          "text-max-width": "160px",
          "text-outline-width": 2,
          "text-outline-color": isDark ? "#0f172a" : "#fff",
          "text-outline-opacity": 0.35,
        } as Record<string, unknown>,
      },
      {
        selector: 'node[graphStyle = "gene"]',
        style: {
          shape: "ellipse",
          padding: "6px",
          width: (ele: { id: () => string }) => {
            const base = 28;
            if (!tapFocusNodeId) return base;
            return isTapCenter(ele.id()) ? 36 : inTapNeighborhood(ele.id()) ? 32 : base;
          },
          height: (ele: { id: () => string }) => {
            const base = 28;
            if (!tapFocusNodeId) return base;
            return isTapCenter(ele.id()) ? 36 : inTapNeighborhood(ele.id()) ? 32 : base;
          },
          "background-color": (ele: { data: (k: string) => string }) =>
            resolveNodeFillColor(ele.data("color"), "Gene", isDark) || (isDark ? "#fde047" : "#facc15"),
          "border-width": (ele: { id: () => string; data: (k: string) => string }) => {
            if (!nodeInSearchHighlight(ele)) return 0;
            if (tapFocusNodeId) {
              if (inTapNeighborhood(ele.id())) return 1;
              if (nodeSearchRing(ele)) return 3;
              return 0;
            }
            if (nodeSearchRing(ele)) return 3;
            return 0;
          },
          "border-color": (ele: { id: () => string; data: (k: string) => string }) => {
            if (!nodeInSearchHighlight(ele)) return isDark ? "#fde047" : "#facc15";
            if (tapFocusNodeId) {
              if (isTapCenter(ele.id())) return tapFocusBorderCenter;
              if (inTapNeighborhood(ele.id())) return tapFocusBorderNeighbor;
            }
            if (nodeSearchRing(ele)) return searchRingColor;
            return isDark ? "#fde047" : "#facc15";
          },
          label: "data(label)",
          "text-valign": "top",
          "text-margin-y": -2,
          "font-size": `${NODE_LABEL_FONT_SIZE}px`,
          "font-weight": 600,
          "text-max-width": "120px",
          color: labelColor,
          "text-outline-width": 2,
          "text-outline-color": isDark ? "#0f172a" : "#fff",
          "text-outline-opacity": 0.45,
        } as Record<string, unknown>,
      },
      {
        selector: 'node[graphStyle = "entity"]',
        style: {
          shape: "ellipse",
          padding: "6px",
          width: (ele: { id: () => string }) => {
            const base = 32;
            if (!tapFocusNodeId) return base;
            return isTapCenter(ele.id()) ? 40 : inTapNeighborhood(ele.id()) ? 36 : base;
          },
          height: (ele: { id: () => string }) => {
            const base = 32;
            if (!tapFocusNodeId) return base;
            return isTapCenter(ele.id()) ? 40 : inTapNeighborhood(ele.id()) ? 36 : base;
          },
          "background-color": (ele: { data: (k: string) => string }) =>
            resolveNodeFillColor(ele.data("color"), ele.data("nodeType") || "", isDark),
          "border-width": (ele: { id: () => string; data: (k: string) => string }) => {
            if (!nodeInSearchHighlight(ele)) return 0;
            if (tapFocusNodeId) {
              if (inTapNeighborhood(ele.id())) return 1;
              if (nodeSearchRing(ele)) return 3;
              return 0;
            }
            if (nodeSearchRing(ele)) return 3;
            return 0;
          },
          "border-color": (ele: { id: () => string; data: (k: string) => string }) => {
            const fill = resolveNodeFillColor(
              ele.data("color"),
              ele.data("nodeType") || "",
              isDark
            );
            if (!nodeInSearchHighlight(ele)) return fill;
            if (tapFocusNodeId) {
              if (isTapCenter(ele.id())) return tapFocusBorderCenter;
              if (inTapNeighborhood(ele.id())) return tapFocusBorderNeighbor;
            }
            if (nodeSearchRing(ele)) return searchRingColor;
            return fill;
          },
          color: labelColor,
          "text-valign": "top",
          "text-halign": "center",
          "text-margin-y": -2,
          "font-size": `${NODE_LABEL_FONT_SIZE}px`,
          "font-weight": 500,
          "text-max-width": "168px",
          "text-outline-width": 2,
          "text-outline-color": isDark ? "#0f172a" : "#fff",
          "text-outline-opacity": 0.3,
        } as Record<string, unknown>,
      },
      {
        selector: "node:active",
        style: {
          "overlay-color": isDark ? "#64748b" : "#94a3b8",
          "overlay-padding": 8,
          "overlay-opacity": 0.2,
        } as Record<string, unknown>,
      },
      {
        selector: "node.dragging",
        style: {
          "border-width": 0,
          "overlay-opacity": 0.25,
          "overlay-padding": 6,
          "overlay-color": isDark ? "#60a5fa" : "#3b82f6",
        } as Record<string, unknown>,
      },
      {
        selector: 'edge[edgeType = "gene-disease"]',
        style: {
          "line-style": "dashed",
          "source-arrow-shape": "none",
          "target-arrow-shape": "triangle",
          "arrow-scale": 0.72,
          "curve-style": "straight",
          label: "data(caption)",
          "text-margin-y": 0,
          width: (ele: EdgeEle) => {
            if (searchTerms.length > 0 && !tapFocusNodeId) return 0;
            if (!edgePassesGraphFilter(ele)) return 0.35;
            if (tapFocusNodeId) return edgeInTapNeighborhood(ele) ? 2.2 : 0.55;
            return isDark ? 1.15 : 1.35;
          },
          "line-color": (ele: EdgeEle) => {
            if (searchTerms.length > 0 && !tapFocusNodeId) return edgeDim;
            if (!edgePassesGraphFilter(ele)) return edgeDim;
            if (!tapFocusNodeId) return edgeLinkBlue;
            return edgeInTapNeighborhood(ele) ? edgeLinkBlue : edgeDim;
          },
          "target-arrow-color": (ele: EdgeEle) => {
            if (searchTerms.length > 0 && !tapFocusNodeId) return edgeDim;
            if (!edgePassesGraphFilter(ele)) return edgeDim;
            if (!tapFocusNodeId) return edgeLinkBlue;
            return edgeInTapNeighborhood(ele) ? edgeLinkBlue : edgeDim;
          },
          "line-opacity": (ele: EdgeEle) => edgeLineOpacity(ele),
          "font-size": `${EDGE_LABEL_FONT_SIZE}px`,
          "font-weight": 500,
          "font-family": "system-ui, -apple-system, sans-serif",
          color: isDark ? edgeLabel : "#334155",
          "text-rotation": "none",
          "text-wrap": "wrap",
          "text-max-width": "220px",
          "text-background-opacity": 0.94,
          "text-background-color": isDark ? "#1e293b" : "#ffffff",
          "text-background-padding": 4,
          "text-border-width": 1,
          "text-border-color": isDark ? "#334155" : "#cbd5e1",
          "text-border-opacity": 1,
          "text-opacity": (ele: EdgeEle) => edgeTextOpacityGeneDisease(ele),
        } as Record<string, unknown>,
      },
      {
        selector: 'edge[edgeType = "gene-gene"]',
        style: {
          "line-style": "solid",
          "source-arrow-shape": "none",
          "target-arrow-shape": "triangle",
          "arrow-scale": 0.72,
          "curve-style": "straight",
          width: (ele: EdgeEle) => {
            if (searchTerms.length > 0 && !tapFocusNodeId) return 0;
            if (!edgePassesGraphFilter(ele)) return 0.35;
            if (tapFocusNodeId) return edgeInTapNeighborhood(ele) ? 2.6 : 0.55;
            return isDark ? 1.15 : 1.35;
          },
          "line-color": (ele: EdgeEle) => {
            if (searchTerms.length > 0 && !tapFocusNodeId) return edgeDim;
            if (!edgePassesGraphFilter(ele)) return edgeDim;
            if (!tapFocusNodeId) return edgeLinkAmber;
            return edgeInTapNeighborhood(ele) ? edgeLinkAmber : edgeDim;
          },
          "target-arrow-color": (ele: EdgeEle) => {
            if (searchTerms.length > 0 && !tapFocusNodeId) return edgeDim;
            if (!edgePassesGraphFilter(ele)) return edgeDim;
            if (!tapFocusNodeId) return edgeLinkAmber;
            return edgeInTapNeighborhood(ele) ? edgeLinkAmber : edgeDim;
          },
          "line-opacity": (ele: EdgeEle) => edgeLineOpacity(ele),
          label: "data(caption)",
          "font-size": `${EDGE_LABEL_FONT_SIZE}px`,
          "font-weight": 500,
          "font-family": "system-ui, -apple-system, sans-serif",
          color: edgeLabel,
          "text-rotation": "none",
          "text-margin-y": 0,
          "text-wrap": "wrap",
          "text-max-width": "120px",
          "text-outline-width": 1,
          "text-outline-color": isDark ? "#0f172a" : "#f8fafc",
          "text-outline-opacity": 0.9,
          "text-opacity": (ele: EdgeEle) => edgeTextOpacityGeneGene(ele),
        } as Record<string, unknown>,
      },
      {
        selector: 'edge[edgeType = "relationship"]',
        style: {
          "line-style": "solid",
          "source-arrow-shape": "none",
          "target-arrow-shape": "triangle",
          "arrow-scale": 0.72,
          "curve-style": "straight",
          width: (ele: EdgeEle) => {
            if (searchTerms.length > 0) return 0;
            if (!edgePassesGraphFilter(ele)) return 0.35;
            if (tapFocusNodeId) return edgeInTapNeighborhood(ele) ? 2.2 : 0.55;
            return isDark ? 1.15 : 1.35;
          },
          "line-color": (ele: EdgeEle) => {
            if (searchTerms.length > 0) return edgeDim;
            if (!edgePassesGraphFilter(ele)) return edgeDim;
            if (!tapFocusNodeId) return edgeLinkBlue;
            return edgeInTapNeighborhood(ele) ? edgeLinkBlue : edgeDim;
          },
          "target-arrow-color": (ele: EdgeEle) => {
            if (searchTerms.length > 0) return edgeDim;
            if (!edgePassesGraphFilter(ele)) return edgeDim;
            if (!tapFocusNodeId) return edgeLinkBlue;
            return edgeInTapNeighborhood(ele) ? edgeLinkBlue : edgeDim;
          },
          "line-opacity": (ele: EdgeEle) => edgeLineOpacity(ele),
          label: "data(caption)",
          "font-size": `${EDGE_LABEL_FONT_SIZE}px`,
          "font-weight": 500,
          "font-family": "system-ui, -apple-system, sans-serif",
          color: edgeLabel,
          "text-rotation": "none",
          "text-margin-y": 0,
          "text-wrap": "wrap",
          "text-max-width": "120px",
          "text-outline-width": 1,
          "text-outline-color": isDark ? "#0f172a" : "#f8fafc",
          "text-outline-opacity": 0.9,
          "text-opacity": (ele: EdgeEle) => edgeTextOpacityRelationship(ele),
        } as Record<string, unknown>,
      },
    ];
  }, [
    isDark,
    tapFocusNodeId,
    tapNeighborIds,
    selectedNodeCategories,
    selectedEdgeCaptions,
    searchTerms,
    highlightNodeIds,
    searchSeeds,
  ]);

  useEffect(() => {
    const el = graphSurfaceRef.current ?? containerRef.current;
    if (!el) return;
    let debounce: ReturnType<typeof setTimeout> | undefined;
    const ro = new ResizeObserver(() => {
      if (debounce) clearTimeout(debounce);
      debounce = setTimeout(() => {
        const cy = cyRef.current;
        if (!cy) return;
        /** Match container size only. Avoid `fit` here: e.g. opening node details resizes the graph column and `fit` would zoom/pan the whole graph (feels like spring/spin on tap). */
        cy.resize();
      }, 50);
    });
    ro.observe(el);
    return () => {
      ro.disconnect();
      if (debounce) clearTimeout(debounce);
    };
  }, [pathData.path]);

  useEffect(() => {
    const onWinResize = () => {
      requestAnimationFrame(() => {
        const cy = cyRef.current;
        if (!cy) return;
        applyViewportFit(cy);
      });
    };
    window.addEventListener("resize", onWinResize);
    /** Mobile URL bar show/hide changes the visible viewport without always firing `resize`. */
    const vv = window.visualViewport;
    vv?.addEventListener("resize", onWinResize);
    return () => {
      window.removeEventListener("resize", onWinResize);
      vv?.removeEventListener("resize", onWinResize);
    };
  }, [applyViewportFit]);

  const runLayout = useCallback((opts?: { animate?: boolean }) => {
    const cy = cyRef.current;
    const el = graphSurfaceRef.current ?? containerRef.current;
    if (!cy || !el) return;
    const animate = opts?.animate ?? false;
    cy.resize();
    const rect = el.getBoundingClientRect();
    const w = Math.max(rect.width, cy.width(), 120);
    const h = Math.max(rect.height, cy.height(), 120);

    /** CoSE: shorter preferred edges + stronger repulsion so genes do not stack; `padding` on nodes adds gap. */
    const layout = cy.layout({
      name: "cose",
      animate,
      ...(animate ? { animationDuration: 900 } : {}),
      fit: true,
      padding: 36,
      boundingBox: { x1: 0, y1: 0, w, h },
      nodeDimensionsIncludeLabels: true,
      randomize: true,
      componentSpacing: 80,
      nodeOverlap: 88,
      /** Stronger than default so enlarged gene ellipses stay separated. */
      nodeRepulsion: () => 8e6,
      idealEdgeLength: (e: { source: () => { degree: () => number } }) => {
        const d = e.source().degree();
        /** Cap length so high-degree hubs do not pull neighbors to the far edge of the canvas. */
        return Math.min(46, 30 + 3 * Math.min(d, 8));
      },
      edgeElasticity: 0.5,
      nestingFactor: 1.15,
      gravity: 0.42,
      gravityRange: 2.2,
      numIter: 2800,
      initialTemp: 200,
      coolingFactor: 0.95,
      minTemp: 1.0,
    } as Parameters<Core["layout"]>[0]);
    layout.on("layoutstop", () => {
      separateOverlappingGeneNodes(cy);
      applyViewportFit(cy);
    });
    layout.run();
  }, [applyViewportFit]);

  /** Track previous tap + path so we can tell "user closed details" from "pathway changed" (remount handles the latter). */
  const prevTapFocusRef = useRef<string | null | undefined>(undefined);
  const prevPathForNodeDetailsRef = useRef(pathData.path);

  /**
   * Animate CoSE when opening details from closed (null → node) or when closing (node → null).
   * In fullscreen, use non-animated layout so a 900ms CoSE does not overlap exiting fullscreen
   * (cy.stop + refits mid-animation left the graph off the canvas).
   * Only switching the selected node while details stay open uses static resize + fit (no animation).
   */
  useLayoutEffect(() => {
    const pathChanged = prevPathForNodeDetailsRef.current !== pathData.path;
    prevPathForNodeDetailsRef.current = pathData.path;

    const prevTap = prevTapFocusRef.current;
    const openedFromClosed = prevTap == null && tapFocusNodeId !== null;
    const userClosedDetails = prevTap != null && tapFocusNodeId === null;
    const switchedNodeWhileOpen =
      prevTap != null && tapFocusNodeId !== null && prevTap !== tapFocusNodeId;
    prevTapFocusRef.current = tapFocusNodeId;

    if (pathChanged) return undefined;

    if (openedFromClosed || userClosedDetails) {
      const inFullscreen = !!document.fullscreenElement;
      let t: ReturnType<typeof setTimeout> | undefined;
      const id = requestAnimationFrame(() => {
        t = setTimeout(() => {
          runLayout({ animate: !inFullscreen });
        }, 100);
      });
      return () => {
        cancelAnimationFrame(id);
        if (t) clearTimeout(t);
      };
    }

    if (switchedNodeWhileOpen) {
      let t: ReturnType<typeof setTimeout> | undefined;
      const id = requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const run = () => {
            const cy = cyRef.current;
            if (!cy) return;
            applyViewportFit(cy);
          };
          run();
          t = setTimeout(run, 80);
        });
      });
      return () => {
        cancelAnimationFrame(id);
        if (t) clearTimeout(t);
      };
    }

    return undefined;
  }, [tapFocusNodeId, pathData.path, runLayout, applyViewportFit]);

  useEffect(() => {
    const onFullscreenChange = () => {
      const fullscreen = !!document.fullscreenElement;
      setIsFullscreen(fullscreen);

      /**
       * Enter + exit: animated CoSE (`runLayout` applies resize + fit on `layoutstop`).
       * On exit, `cy.stop()` clears in-flight layout; avoid extra `fit` during the ~900ms animation
       * (earlier staggered fits locked a bad viewport). One late refit covers slow container settle.
       */
      const delayBeforeLayout = fullscreen ? 100 : 280;
      const layoutAnimMs = 900;
      requestAnimationFrame(() => {
        setTimeout(() => {
          const cy = cyRef.current;
          if (cy) {
            if (!fullscreen) cy.stop();
            runLayout({ animate: true });
          }
          if (fullscreen) {
            setTimeout(() => {
              const c2 = cyRef.current;
              if (c2) applyViewportFit(c2);
            }, 350);
          } else {
            /** After animated CoSE (~900ms); `layoutstop` already fits — this catches slow flex resize. */
            setTimeout(() => {
              const c2 = cyRef.current;
              if (c2) applyViewportFit(c2);
            }, layoutAnimMs + 120);
          }
          if (!fullscreen && containerRef.current) {
            containerRef.current.focus();
          }
        }, delayBeforeLayout);
      });
    };
    document.addEventListener("fullscreenchange", onFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", onFullscreenChange);
  }, [runLayout, applyViewportFit]);

  const handleDownload = useCallback(() => {
    const cy = cyRef.current;
    if (!cy) return;
    const png = cy.png({
      output: "blob",
      scale: 2,
      bg: isDark ? "#1e293b" : "#f8fafc",
    });
    const url = URL.createObjectURL(png);
    const link = document.createElement("a");
    link.href = url;
    link.download = `pathway-${pathData.path.replace(/[^a-zA-Z0-9]/g, "_")}-graph.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [isDark, pathData.path]);

  const handleZoomIn = useCallback(() => {
    const cy = cyRef.current;
    if (!cy) return;
    const z = cy.zoom();
    cy.zoom({ level: Math.min(z * 1.25, cy.maxZoom()), renderedPosition: { x: cy.width() / 2, y: cy.height() / 2 } });
  }, []);

  const handleZoomOut = useCallback(() => {
    const cy = cyRef.current;
    if (!cy) return;
    const z = cy.zoom();
    cy.zoom({ level: Math.max(z / 1.25, cy.minZoom()), renderedPosition: { x: cy.width() / 2, y: cy.height() / 2 } });
  }, []);

  const handleZoomFit = useCallback(() => {
    const cy = cyRef.current;
    if (!cy) return;
    applyViewportFit(cy);
  }, [applyViewportFit]);

  const handleFullscreen = useCallback(async () => {
    if (!containerRef.current) return;
    try {
      if (!document.fullscreenElement) {
        await containerRef.current.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch (e) {
      console.error("Fullscreen failed:", e);
    }
  }, []);

  const nodeTypeFilterRows = useMemo(() => {
    const rows: { key: NodeFilterCategory; label: string }[] = [...NODE_TYPE_FILTER_ROWS];
    if (filterMeta.categoryCounts.other > 0) {
      rows.push({ key: "other", label: "Other" });
    }
    return rows;
  }, [filterMeta]);

  const toggleNodeCategory = useCallback((key: NodeFilterCategory) => {
    setSelectedNodeCategories((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const selectAllNodeCategories = useCallback(() => {
    const keys = (Object.entries(filterMeta.categoryCounts) as [NodeFilterCategory, number][])
      .filter(([, c]) => c > 0)
      .map(([k]) => k);
    setSelectedNodeCategories(new Set(keys));
  }, [filterMeta.categoryCounts]);

  const toggleEdgeCaption = useCallback((cap: string) => {
    setSelectedEdgeCaptions((prev) => {
      const next = new Set(prev);
      if (next.has(cap)) next.delete(cap);
      else next.add(cap);
      return next;
    });
  }, []);

  const selectAllEdgeCaptions = useCallback(() => {
    setSelectedEdgeCaptions(new Set(filterMeta.edgeCaptions));
  }, [filterMeta.edgeCaptions]);

  useEffect(() => {
    if (!searchPanelOpen) return;
    const onDocMouseDown = (e: MouseEvent) => {
      const el = e.target as Node;
      if (searchPanelRef.current?.contains(el)) return;
      if (searchButtonRef.current?.contains(el)) return;
      setSearchPanelOpen(false);
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [searchPanelOpen]);

  useEffect(() => {
    if (!searchPanelOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setSearchPanelOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [searchPanelOpen]);

  const onCy = useCallback(
    (cy: Core) => {
      cyRef.current = cy;
      if (eventListenersCyRef.current !== cy) {
        eventListenersCyRef.current = cy;
        cy.nodes().grabify();
        cy.on("grab", "node", (evt: { target: { addClass: (c: string) => void } }) => {
          evt.target.addClass("dragging");
        });
        cy.on("free", "node", (evt: { target: { removeClass: (c: string) => void } }) => {
          evt.target.removeClass("dragging");
        });
        cy.on("tap", "node", (evt: { target: { id: () => string; data: (k: string) => string; neighborhood: (s: string) => { map: (fn: (x: { id: () => string; data: (k: string) => string }) => string) => string[] } } }) => {
          const node = evt.target;
          const id = node.id();
          const nbrNodes = node.neighborhood("node");
          setTapFocusNodeId(id);
          setTapNeighborIds(nbrNodes.map((x) => x.id()));
        });
        cy.on("tap", (evt: { target: Core }) => {
          if (evt.target === cy) {
            setTapFocusNodeId(null);
            setTapNeighborIds([]);
          }
        });
        /** `react-cytoscapejs` invokes `cy()` on every prop update; layout must run only once per instance. */
        requestAnimationFrame(() => {
          requestAnimationFrame(() => runLayout());
        });
      }
    },
    [runLayout]
  );

  const clearNodeDetails = useCallback(() => {
    setTapFocusNodeId(null);
    setTapNeighborIds([]);
  }, []);

  return (
    <div
      ref={containerRef}
      tabIndex={-1}
      className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg relative outline-none w-full max-w-full min-w-0 min-h-0 flex flex-col overflow-hidden
        h-[clamp(300px,min(75dvh,calc(100dvh-9rem)),900px)]
        max-h-[min(900px,calc(100dvh-7rem))] sm:max-h-[min(900px,calc(100dvh-8rem))]"
      style={{ padding: 0, boxSizing: "border-box" }}
    >
      <div className="flex min-h-0 min-w-0 flex-1 flex-col lg:flex-row">
        <div className="relative flex min-h-[min(280px,55vh)] min-w-0 flex-1 flex-col overflow-hidden lg:min-h-0">
          <div className="pointer-events-none absolute top-2 left-2 z-30 max-w-[min(340px,85vw)]">
            <div className="truncate rounded bg-black/70 px-2 py-1.5 text-xs font-medium text-white">
              {pathData.path}
            </div>
          </div>

      {/* Top-right: search + layout / export — match zoom / fit control sizing */}
      <div className="absolute top-2 right-2 z-40 flex max-w-[calc(100%-4.5rem)] flex-col items-end gap-2 sm:max-w-none">
        <div
          className="flex flex-row flex-nowrap items-center overflow-hidden rounded-md border border-gray-200/80 bg-white/90 shadow-md dark:border-slate-600 dark:bg-slate-800/90"
          role="toolbar"
          aria-label="Graph actions"
        >
          <button
            ref={searchButtonRef}
            type="button"
            onClick={() => setSearchPanelOpen((o) => !o)}
            className="p-1.5 text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-slate-700"
            title="Search and filter graph"
            aria-label="Search and filter graph"
            aria-expanded={searchPanelOpen}
          >
            <MagnifyingGlassIcon className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={handleFullscreen}
            className="border-l border-gray-200/80 p-1.5 text-gray-700 hover:bg-gray-100 dark:border-slate-600 dark:text-gray-200 dark:hover:bg-slate-700"
            title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
            aria-label={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
          >
            {isFullscreen ? (
              <ArrowsPointingInIcon className="h-5 w-5" />
            ) : (
              <ArrowsPointingOutIcon className="h-5 w-5" />
            )}
          </button>
          <button
            type="button"
            onClick={handleDownload}
            className="border-l border-gray-200/80 p-1.5 text-gray-700 hover:bg-gray-100 dark:border-slate-600 dark:text-gray-200 dark:hover:bg-slate-700"
            title="Download graph as PNG"
            aria-label="Download graph as PNG"
          >
            <ArrowDownTrayIcon className="h-5 w-5" />
          </button>
        </div>

        {searchPanelOpen && (
          <div
            ref={searchPanelRef}
            className="w-[min(100vw-1rem,26rem)] rounded-lg border border-gray-200 bg-white p-4 shadow-lg dark:border-slate-600 dark:bg-slate-900"
            role="dialog"
            aria-label="Search graph"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="mb-3 flex items-center justify-between gap-2">
              <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                Search & filter
              </span>
              <button
                type="button"
                onClick={() => setSearchPanelOpen(false)}
                className="rounded p-1 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-slate-700"
                title="Close"
                aria-label="Close search panel"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <input
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search genes, pathways, nodes (comma-separated)..."
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-800 dark:text-gray-100 dark:placeholder:text-slate-500"
            />

            <div className="mt-5">
              <div className="mb-2 flex items-center justify-between gap-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-400">
                  Node types:
                </span>
                <button
                  type="button"
                  onClick={selectAllNodeCategories}
                  className="text-xs font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  Select all
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {nodeTypeFilterRows.map(({ key, label }) => {
                  const count = filterMeta.categoryCounts[key];
                  const disabled = count === 0;
                  const active = selectedNodeCategories.has(key);
                  return (
                    <button
                      key={key}
                      type="button"
                      disabled={disabled}
                      onClick={() => toggleNodeCategory(key)}
                      className={`rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors ${
                        disabled
                          ? "cursor-not-allowed border-slate-200 text-slate-400 opacity-60 dark:border-slate-700 dark:text-slate-500"
                          : active
                            ? "border-slate-400 bg-slate-100 text-slate-900 dark:border-slate-500 dark:bg-slate-700 dark:text-white"
                            : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700/50"
                      }`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>

            {filterMeta.edgeCaptions.length > 0 && (
              <div className="mt-5">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-400">
                    Edge types:
                  </span>
                  <button
                    type="button"
                    onClick={selectAllEdgeCaptions}
                    className="text-xs font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                  >
                    Select all
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {filterMeta.edgeCaptions.map((cap) => {
                    const active = selectedEdgeCaptions.has(cap);
                    return (
                      <button
                        key={cap}
                        type="button"
                        onClick={() => toggleEdgeCaption(cap)}
                        className={`rounded-md border px-2.5 py-1.5 text-[0.65rem] font-semibold uppercase tracking-wide transition-colors ${
                          active
                            ? "border-slate-400 bg-slate-100 text-slate-900 dark:border-slate-500 dark:bg-slate-700 dark:text-white"
                            : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700/50"
                        }`}
                      >
                        {cap}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bottom-left: zoom / fit — keep clear of path chip on narrow screens */}
      <div
        className="absolute bottom-2 left-2 z-30 flex flex-col gap-0.5 sm:gap-1 rounded-md border border-gray-200/80 bg-white/90 p-0.5 sm:p-1 shadow-md dark:border-slate-600 dark:bg-slate-800/90 max-sm:bottom-auto max-sm:left-auto max-sm:right-2 max-sm:top-14"
        role="toolbar"
        aria-label="Zoom and fit"
      >
        <button
          type="button"
          onClick={handleZoomIn}
          className="rounded p-1.5 text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-slate-700"
          title="Zoom in"
          aria-label="Zoom in"
        >
          <MagnifyingGlassPlusIcon className="h-5 w-5" />
        </button>
        <button
          type="button"
          onClick={handleZoomOut}
          className="rounded p-1.5 text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-slate-700"
          title="Zoom out"
          aria-label="Zoom out"
        >
          <MagnifyingGlassMinusIcon className="h-5 w-5" />
        </button>
        <button
          type="button"
          onClick={handleZoomFit}
          className="rounded p-1.5 text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-slate-700"
          title="Fit to view"
          aria-label="Fit to view"
        >
          <ViewfinderCircleIcon className="h-5 w-5" />
        </button>
      </div>

          {/* Flex fill gives Cytoscape a real height; overflow-hidden keeps the canvas from painting under the node-details aside. */}
          <div className="relative z-0 flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
            <div
              ref={graphSurfaceRef}
              className="relative isolate min-h-0 w-full min-w-0 flex-1 overflow-hidden rounded-[inherit] touch-none bg-gradient-to-br from-slate-50 to-slate-100/90 dark:from-slate-800/95 dark:to-slate-900/90"
            >
              <CytoscapeComponent
                key={pathData.path}
                elements={elements}
                stylesheet={stylesheet as never}
                cy={onCy}
                style={{
                  display: "block",
                  width: "100%",
                  height: "100%",
                  minWidth: 0,
                  maxWidth: "100%",
                  minHeight: 0,
                  boxSizing: "border-box",
                }}
                zoomingEnabled
                minZoom={0.2}
                maxZoom={3}
                boxSelectionEnabled={false}
                userPanningEnabled
                userZoomingEnabled
              />
            </div>
          </div>
        </div>

        {selectedNodeDetails && (
          <aside className="relative z-10 flex max-h-[min(380px,50vh)] w-full shrink-0 flex-col border-t border-gray-200 bg-white dark:border-slate-600 dark:bg-slate-900 lg:max-h-none lg:h-auto lg:min-h-0 lg:w-96 lg:max-w-[min(24rem,calc(100vw-2rem))] lg:border-l lg:border-t-0">
            <div className="flex shrink-0 items-center justify-between border-b border-gray-200 px-4 py-3 dark:border-slate-600">
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                Node Details
              </h3>
              <button
                type="button"
                onClick={clearNodeDetails}
                className="rounded p-1 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-slate-700"
                aria-label="Close node details"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
              <h4 className="text-xl font-bold leading-tight text-gray-900 dark:text-white">
                {selectedNodeDetails.node.caption}
              </h4>
              <div className="mt-5">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Type
                </p>
                <span
                  className={`mt-1.5 inline-block rounded-full px-2.5 py-1 text-xs font-semibold uppercase ${typeBadgeClassName(
                    selectedNodeDetails.node.properties?.node_type || ""
                  )}`}
                >
                  {(selectedNodeDetails.node.properties?.node_type || "Unknown").replace(/_/g, " ")}
                </span>
              </div>
              <div className="mt-6">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Description
                </p>
                <p className="mt-2 text-sm leading-relaxed text-gray-700 dark:text-gray-300">
                  {selectedNodeDetails.node.properties?.description?.trim()
                    ? selectedNodeDetails.node.properties.description
                    : "No description available."}
                </p>
              </div>
              <div className="mt-6">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Connected nodes ({selectedNodeDetails.totalConnected})
                </p>
                {selectedNodeDetails.totalConnected === 0 ? (
                  <p className="mt-2 text-sm italic text-gray-500 dark:text-gray-400">
                    No other nodes in this subgraph.
                  </p>
                ) : (
                  <div className="mt-3 space-y-5">
                    {selectedNodeDetails.buckets.genes.length > 0 && (
                      <div>
                        <p className="mb-2 text-xs font-medium text-gray-600 dark:text-gray-300">
                          Genes ({selectedNodeDetails.buckets.genes.length})
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {selectedNodeDetails.buckets.genes.map((name, i) => (
                            <span
                              key={`g-${name}-${i}`}
                              className="inline-flex rounded-md bg-gray-100 px-2.5 py-1.5 text-xs font-medium text-gray-800 dark:bg-slate-700 dark:text-gray-100"
                            >
                              {name}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {selectedNodeDetails.buckets.diseases.length > 0 && (
                      <div>
                        <p className="mb-2 text-xs font-medium text-gray-600 dark:text-gray-300">
                          Diseases ({selectedNodeDetails.buckets.diseases.length})
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {selectedNodeDetails.buckets.diseases.map((name, i) => (
                            <span
                              key={`d-${name}-${i}`}
                              className="inline-flex rounded-md bg-gray-100 px-2.5 py-1.5 text-xs font-medium text-gray-800 dark:bg-slate-700 dark:text-gray-100"
                            >
                              {name}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {selectedNodeDetails.buckets.otherByType.map((group) => (
                      <div key={group.typeKey}>
                        <p className="mb-2 text-xs font-medium text-gray-600 dark:text-gray-300">
                          {group.displayName} ({group.labels.length})
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {group.labels.map((name, i) => (
                            <span
                              key={`${group.typeKey}-${name}-${i}`}
                              className="inline-flex rounded-md bg-gray-100 px-2.5 py-1.5 text-xs font-medium text-gray-800 dark:bg-slate-700 dark:text-gray-100"
                            >
                              {name}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </aside>
        )}
      </div>
    </div>
  );
};

export const PathwayGraph: React.FC<PathwayGraphProps> = ({ pathData }) => {
  if (!pathData) return null;
  return (
    <div className="w-full min-w-0 max-w-full shrink-0 space-y-2">
      <PathwayGraphInner pathData={pathData} />
    </div>
  );
};
